/**
 * The one file-size formatter.
 */
/**
 * A kilobyte is 1024 bytes here.
 *
 * Every scaling size formatter in this repo already divides by 1024, and the
 * size ceilings the product states ("up to 25 MB") are read back from the same
 * API figures those formatters render. Switching to 1000 would restate every
 * size the product currently shows without any of them being wrong today, so
 * 1024 is what converges the call sites at zero visible cost.
 */
const BYTES_PER_UNIT = 1024;
const SCALED_UNITS = ["KB", "MB", "GB", "TB"];
/**
 * The number that will be printed for a quantity of one unit: one decimal below
 * ten, none at or above it.
 *
 * The unit is chosen from this rather than from the raw quotient, because the
 * rounding is what can push a reading into the next unit: 1048575 bytes is
 * 1023.999 KB, and printing that as "1024 KB" names a size no file manager
 * shows and no unit that exists.
 */
function printed(value) {
    return value >= 10 ? Math.round(value) : Number(value.toFixed(1));
}
/**
 * A byte count as a size a person can check against their own file manager:
 * "0 B", "824 B", "1.5 KB", "40 MB", "2.5 GB".
 *
 * One decimal below 10 of a unit and none at or above it — enough precision to
 * tell two files apart, not so much that a table column turns into digits.
 * Exact, never rounded up to a friendlier story.
 *
 * Nullish, non-finite and negative input gives `""`, so a row with no size
 * renders as nothing rather than as a wrong number.
 */
export function formatBytes(bytes) {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
        return "";
    }
    // Bytes are whole: a file manager shows "825 B", never "824.6 B". Every
    // scaled unit carries the decimal instead.
    const whole = Math.round(bytes);
    if (whole < BYTES_PER_UNIT)
        return `${whole} B`;
    let value = bytes;
    let unit = "B";
    for (const next of SCALED_UNITS) {
        if (printed(value) < BYTES_PER_UNIT)
            break;
        value /= BYTES_PER_UNIT;
        unit = next;
    }
    // `printed` has already quantized to at most one decimal, so a whole result
    // carries no trailing ".0" to strip.
    return `${printed(value)} ${unit}`;
}
